import React, { forwardRef, useMemo, useCallback, useState, useRef } from "react";
import _ from "lodash";
import { Form, Tooltip, Alert } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, nameRegexValidator, urlRegexValidator } from "utils/formUtils";
import message from "components/CustomMessage";
import { HelpSolidIcon } from "components/Icons";
import BaseModalForm from "components/BaseModalForm";
import { duplicateNameValidator } from "utils/validatorUtil";
import AutoMappingSettingsForm, { GIT_TOOL_MAPPING_CONFIG } from "./common/AutoMappingSettingsForm";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

const hostUrlTools = ['GITLAB'];
const defaultHostUrl = {
    GITLAB: 'https://gitlab.com'
}
const GitRepoConfigForm = forwardRef( ({afterClose, configData, onUpdateConfigComponent, ...props}, ref) => {
    const featureFlags = useFeatureFlag();
    const toolConfigs = useRef();
    const isEdit = !_.isUndefined(configData);
    const [form] = Form.useForm();
    const {configList} = props.id
    const toolName = isEdit ? configData.repoType : _.toUpper(props?.id);
    const [hostingType, setHostingType] = useState("Cloud");

    const initialConfig = useMemo(() => {
        const config = {};
        _.forEach(configData?.installationConfig?.subProductMappingConfig, entry => config[entry] = true);
        config.codeClassificationEnabled = !!props.codeClassificationinitialConfig?.codeClassificationEnabled || !!configData?.installationConfig?.codeClassificationEnabled || false;
        config.webhookEnabled = configData?.installationConfig?.webhookEnabled;
        if (toolName === "GITLAB") {
            config.mapArchived = !!configData?.installationConfig?.mapArchived;
        }
        toolConfigs.current = config;
        return config;
      }, [configData, props.codeClassificationinitialConfig, toolName]);

    const saveSettings = useCallback(async (id) => {
        const configs = toolConfigs.current;
        const payload = {
            subProductMappingConfig: featureFlags.SIMPLIFIED_ONBOARDING ?
              _.filter([
                GIT_TOOL_MAPPING_CONFIG.MAP_INACTIVE_REPOS,
                GIT_TOOL_MAPPING_CONFIG.MAP_DORMANT_REPOS
              ], (entry) => configs?.[entry]) : [],
            codeClassificationEnabled: configs?.codeClassificationEnabled,
            webhookEnabled: configs?.webhookEnabled,
            ingestComponents: configs?.ingestComponents,
          }
      
        await ToolsIntegrationsAPI.getGithubInstallationConfigs(id, payload).promise;
      }, [featureFlags.SIMPLIFIED_ONBOARDING]);

    const submitData = async () => {
        await form.validateFields();
        const values = form.getFieldsValue();
        const gitlabInstallationConfig = toolName === "GITLAB" ? { installationConfig: { mapArchived: toolConfigs.current?.mapArchived } } : {};
        if (isEdit) {
          if (toolName !== "BITBUCKET") {
            const dataToSend = { ...values, id: configData.id, repoType: toolName, token: values.secretKey || values.password || values.token, ...gitlabInstallationConfig };
            await ToolsIntegrationsAPI.updateGitInstallation(_.omit(dataToSend, ["secretKey", "hostingType"])).promise;
        }
          await saveSettings(configData.id);
          onUpdateConfigComponent();
          message.success(`Successfully updated.`);
        } else {
          const data = await ToolsIntegrationsAPI.createGitInstallation({ ...values, repoType: toolName, token: values.password || values.token, ...gitlabInstallationConfig }).promise;
          await saveSettings(data.id);
          message.success(`Successfully configured.`);
        }
      };

    const onSettingsChange = useCallback((changedSetting, settings) => {
        toolConfigs.current = settings;
    }, [])

    const baseModalProps = {
        okText: "Configure",
    }

    const GITLAB_FIELDS = useMemo(() => ([
        {
            name: "name",
            label: "Name",
            props: {
                autoFocus: true
            },
            colSpan: 24,
            required: true,
            rules: [
                nameRegexValidator,
                duplicateNameValidator({ list: configList }),
            ],
        },
        ...(isEdit
            ? [
                {   
                    colSpan: 24,  
                    render: () => <Alert className="m-b-m" message="Please ensure the new token has required permissions to existing repositories present in ArmorCode. 
                    Adding token with incorrect permissions would result in deletion of repositories from ArmorCode." type="warning" />,
                },
              ]
            : []),
        {
            name: "token",
            label: "Token",
            help: "Enter a valid token or Generate it",
            type: FieldTypes.PASSWORD,
            labelAlign: "left",
            colSpan: 24,
            required: true,
        },
        {
            name: "hostUrl",
            label: "Host Url",
            rules: [urlRegexValidator()],
            colSpan: 24,
            required: true,
            disabled: isEdit,
        },
    ]), [configList, isEdit]);
    
    const AZURE_REPOS_FIELDS = useMemo(() => ([
        {
            name: "name",
            label: "Name",
            props: {
                autoFocus: true
            },
            colSpan: 24,
            required: true,
            rules: [
                nameRegexValidator,
                duplicateNameValidator({ list: configList }),
            ],
        },
        ...(isEdit
            ? [
                {   
                    colSpan: 24,
                    render: () => <Alert className="m-b-m" message="Please ensure the new token has required permissions to existing repositories present in ArmorCode. 
                    Adding token with incorrect permissions would result in deletion of repositories from ArmorCode." type="warning" />,
              },
            ]
          : [
              {
                name: "hostingType",
                label: "Hosting Type",
                type: FieldTypes.SELECT,
                colSpan: 24,
                required: true,
                options: [
                  { label: "Cloud", value: "Cloud" },
                  { label: "On-Prem", value: "OnPrem" },
                ],
                props: {
                  onChange: (value) => {
                    setHostingType(value);
                  },
                },
              },
              ...(hostingType === "OnPrem"
                ? [
                    {
                      name: "hostUrl",
                      label: "Host Url",
                      rules: [urlRegexValidator()],
                      colSpan: 24,
                      required: true,
                      disabled: isEdit,
                    },
                  ]
                : []),
            ]),
        {
            name: "secretKey",
            label: "API Token",
            type: FieldTypes.PASSWORD,
            colSpan: 24,
            help: "Enter a valid API Token or Generate it",
            required: true,
        },
        {
            name: "workspace",
            label: hostingType === "Cloud" ? "Organization" : "Collection",
            colSpan: 24,
            required: true,
            disabled: isEdit, 
        },
    ]), [configList, isEdit, hostingType]);
    

    const fieldMap = useMemo(() => ({
        AZURE_REPOS: AZURE_REPOS_FIELDS,
        GITLAB: GITLAB_FIELDS,
        BITBUCKET: []
    }), [AZURE_REPOS_FIELDS, GITLAB_FIELDS]);
    
    const fields = useMemo(() => [
        ...(fieldMap[toolName] || [])
    ], [fieldMap, toolName])

    const onValuesChange = useCallback(
        (_, values) => {
          form.setFieldsValue(values);
        },
        [form]
      );

    return (
        <BaseModalForm             
            modalProps={baseModalProps}
            ref={ref}
            form={form} 
            fields={fields}
            onOk={submitData}
            onValuesChange={onValuesChange}
            afterClose={afterClose}
            title= {`Configure ${_.replace(props?.id, "_", " ")}`}
            // when in edit mode, we are getting hosturl with an extra "/" in the end, using slice to remove it
            initialValues={ { hostUrl: isEdit?configData.hostUrl?.slice(0, -1) : hostUrlTools.includes(toolName)? defaultHostUrl[toolName] : "" , name: configData?.name, workspace: configData?.workspace, hostingType  : "Cloud",} }
        >
          <AutoMappingSettingsForm onChange={onSettingsChange} values={ props.codeClassificationinitialConfig ?? initialConfig} toolName={toolName} />
        </BaseModalForm>
    )
})

export default GitRepoConfigForm
